import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, LogOut, Save, Euro, Truck, RefreshCcw, Sparkles, Package } from "lucide-react";
import { toast, Toaster } from "sonner";

import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import ThemeToggle from "@/components/ThemeToggle";
import { AUTH } from "@/constants/testIds/app";
import { PRICING } from "@/constants/testIds/pricing";

function PriceField({ label, description, testId, value, onChange, icon: Icon, suffix }) {
  return (
    <div className="p-6 rounded-xl border border-[var(--border-c)] bg-[var(--bg-2)]">
      <div className="flex items-start gap-4">
        <div className="h-10 w-10 rounded-full bg-[#00F2F4]/12 text-[#00D6D8] flex items-center justify-center flex-shrink-0">
          <Icon size={16} strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <Label className="text-sm font-serif text-[var(--fg-1)] block">{label}</Label>
          <p className="mt-1 text-xs text-[var(--fg-3)] leading-relaxed">{description}</p>
          <div className="mt-4 flex items-center gap-2">
            <div className="relative flex-1">
              <Euro size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fg-4)]" />
              <Input
                data-testid={testId}
                type="number"
                min="0"
                step="0.01"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="pl-9 font-mono"
              />
            </div>
            {suffix && (
              <span className="text-xs text-[var(--fg-3)] whitespace-nowrap font-sans">{suffix}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminSettings() {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const [firstOrder, setFirstOrder] = useState("");
  const [reorder, setReorder] = useState("");
  const [shipping, setShipping] = useState("");
  const [bundles, setBundles] = useState({ 1: "", 2: "", 3: "", 4: "", 5: "" });
  const [updatedAt, setUpdatedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/card-pricing");
        setFirstOrder(String(data.first_order_price ?? ""));
        setReorder(String(data.reorder_price ?? ""));
        setShipping(String(data.shipping_cost ?? ""));
        const b = data.bundle_prices || {};
        setBundles({
          1: String(b["1"] ?? ""),
          2: String(b["2"] ?? ""),
          3: String(b["3"] ?? ""),
          4: String(b["4"] ?? ""),
          5: String(b["5"] ?? ""),
        });
        setUpdatedAt(data.updated_at);
      } catch {
        toast.error("Errore nel caricamento dei prezzi");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    const fo = Number(firstOrder);
    const ro = Number(reorder);
    const sh = Number(shipping);
    const bundleNums = {};
    for (const k of ["1", "2", "3", "4", "5"]) {
      const v = Number(bundles[k]);
      if (!Number.isFinite(v) || v < 0) {
        toast.error(`Prezzo bundle ${k} card non valido`);
        return;
      }
      bundleNums[k] = v;
    }
    if (![fo, ro, sh].every((n) => Number.isFinite(n) && n >= 0)) {
      toast.error("Inserisci valori numerici validi (≥ 0)");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/card-pricing", {
        first_order_price: fo,
        reorder_price: ro,
        shipping_cost: sh,
        bundle_prices: bundleNums,
      });
      setUpdatedAt(data.updated_at);
      toast.success("Prezzi aggiornati con successo.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-1)]" data-testid={PRICING.page}>
      <Toaster position="top-right" theme={theme} />

      <header className="sticky top-0 z-40 bg-[var(--glass-bg)] backdrop-blur-xl border-b border-[var(--border-c)]">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <div className="font-serif text-3xl text-[var(--fg-1)] tracking-tight leading-none">
              Recen<span className="italic text-[#00F2F4]">Up</span>
            </div>
            <Link
              to="/admin/card-requests"
              className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[var(--fg-3)] hover:text-[var(--fg-1)]"
              style={{ transition: "color 0.2s ease" }}
            >
              <ArrowLeft size={14} /> Richieste
            </Link>
            <div className="hidden md:flex items-center gap-3 text-xs text-[var(--fg-3)] uppercase tracking-[0.2em]">
              <div className="h-4 w-px bg-[var(--border-c)]" />
              Impostazioni admin
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <div className="text-xs text-[var(--fg-3)] font-sans hidden md:block">{user?.email}</div>
            <button
              data-testid={AUTH.logoutBtn}
              onClick={logout}
              className="h-9 w-9 rounded-full hover:bg-[var(--bg-3)] flex items-center justify-center text-[var(--fg-2)]"
              style={{ transition: "background-color 0.2s ease" }}
              title="Esci"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        <div className="mb-8">
          <div className="text-[10px] font-sans uppercase tracking-[0.25em] text-[#00F2F4] font-semibold">
            Impostazioni · Prezzi Card
          </div>
          <h1 className="mt-2 font-serif text-4xl md:text-5xl text-[var(--fg-1)] tracking-tight">
            Prezzi <span className="italic">RecenUp Card</span>
          </h1>
          <p className="mt-2 text-sm text-[var(--fg-3)] max-w-2xl">
            Gestisci i prezzi applicati agli ordini iniziali e ai successivi riordini delle RecenUp Card.
            Le richieste già confermate mantengono il prezzo originale.
          </p>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-[var(--fg-3)]">Caricamento…</div>
        ) : (
          <motion.div
            initial={{ y: 16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="space-y-5"
          >
            <PriceField
              label="Prezzo primo ordine"
              description="Prezzo unitario di fallback per ordini fuori dai bundle standard (es. richieste personalizzate con >5 card)."
              testId={PRICING.firstOrderInput}
              value={firstOrder}
              onChange={setFirstOrder}
              icon={Sparkles}
              suffix="/ card"
            />

            <div className="p-6 rounded-xl border border-[var(--border-c)] bg-[var(--bg-2)]">
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-full bg-[#00F2F4]/12 text-[#00D6D8] flex items-center justify-center flex-shrink-0">
                  <Package size={16} strokeWidth={1.8} />
                </div>
                <div className="flex-1 min-w-0">
                  <Label className="text-sm font-serif text-[var(--fg-1)] block">Prezzi bundle 1–5 card</Label>
                  <p className="mt-1 text-xs text-[var(--fg-3)] leading-relaxed">
                    Prezzo totale (spedizione esclusa) applicato automaticamente al cliente in base alla quantità selezionata. Attivo su primo ordine.
                  </p>
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
                    {["1", "2", "3", "4", "5"].map((k) => (
                      <div key={k}>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)] mb-1.5">
                          {k} card
                        </div>
                        <div className="relative">
                          <Euro size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--fg-4)]" />
                          <Input
                            data-testid={`pricing-bundle-${k}`}
                            type="number"
                            min="0"
                            step="0.01"
                            value={bundles[k]}
                            onChange={(e) => setBundles((prev) => ({ ...prev, [k]: e.target.value }))}
                            className="pl-8 font-mono text-sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <PriceField
              label="Prezzo riordino"
              description="Prezzo applicato per ogni card quando un'attività ha già ricevuto almeno un ordine completato."
              testId={PRICING.reorderInput}
              value={reorder}
              onChange={setReorder}
              icon={RefreshCcw}
              suffix="/ card"
            />
            <PriceField
              label="Costo di spedizione"
              description="Costo applicato all'ordine (indipendente dal numero di card)."
              testId={PRICING.shippingInput}
              value={shipping}
              onChange={setShipping}
              icon={Truck}
              suffix="/ ordine"
            />

            <div className="flex items-center justify-between pt-4">
              <div className="text-xs text-[var(--fg-3)]">
                {updatedAt ? `Ultimo aggiornamento: ${new Date(updatedAt).toLocaleString("it-IT")}` : "Prezzi non ancora salvati"}
              </div>
              <Button
                data-testid={PRICING.saveBtn}
                onClick={save}
                disabled={saving}
                className="rounded-full bg-[var(--fg-1)] hover:bg-[var(--fg-2)] text-[var(--bg-1)] gap-2 px-6"
              >
                <Save size={14} /> {saving ? "Salvataggio…" : "Salva prezzi"}
              </Button>
            </div>

            <div className="mt-8 p-5 rounded-xl bg-[var(--bg-3)]/50 border border-[var(--border-c)] text-xs text-[var(--fg-2)] leading-relaxed">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)] mb-2 font-sans font-semibold">
                Come funziona la classificazione
              </div>
              <ul className="space-y-1.5 list-disc pl-4">
                <li>Un'attività è considerata <strong>Primo ordine</strong> finché non ha almeno una richiesta con stato <em>Consegnata</em>.</li>
                <li>Il prezzo <strong>Riordino</strong> scatta solo dopo la prima consegna effettiva. Richieste annullate, non pagate o mai consegnate non contano.</li>
                <li>Al momento dell'approvazione admin di una richiesta, il prezzo del listino viene bloccato nella richiesta. Modifiche future al listino non retroagiscono.</li>
              </ul>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
